import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBookmarkStore } from '@/lib/bookmark-store';
import { useHighlightStore } from '@/lib/highlight-store';
import { useAdiaStore } from '@/lib/store';
import {
  BIBLE_BOOKMARKS_ENABLED,
  BIBLE_READER_V2_ENABLED,
  PRAY_MODE_ENABLED,
} from '@/lib/feature-flags';
import { getAuthUserId, waitForAuthResolution } from '@/lib/keepsake/epochs';
import {
  journalSourcePolicy,
  type JournalSourcePolicy,
} from '@/lib/journal-feature-policy';
import type { JournalSources } from '@/lib/journal-aggregate';

/**
 * Loads the three Scripture Journal sources from IDB and reports their shared
 * state. A failed enabled source is never collapsed into an apparently empty
 * journal: callers render an explicit retry state until all enabled reads have
 * completed successfully.
 *
 * Pages MUST NOT treat an empty aggregate as truly empty (and redirect up/home)
 * until `ready` is true. On a direct load or refresh the stores can still be
 * mid-hydration on the first render (main.tsx kicks the boot loads
 * fire-and-forget), so an early empty-redirect would send a user away from a
 * valid book/passage URL even though IDB holds marks, including prayer-only
 * marks (codex Phase 1 BLOCKER).
 *
 * Disabled or unauthenticated sources contribute `[]` and are not awaited.
 * The loads are idempotent owner-bound re-reads of IDB (defense in depth; boot
 * hydration already ran), so calling them per Journal page mount is safe.
 * Disabled sources are neither read nor exposed. This matters for staged
 * rollouts: enabling prayer-only Journal must not touch bookmark/highlight IDB,
 * and a signed-out fallback must not expose any authored source.
 */
export type JournalSourcesState = 'loading' | 'ready' | 'error';

export function useJournalSources(): {
  sources: JournalSources;
  state: JournalSourcesState;
  ready: boolean;
  retry: () => void;
} {
  const bookmarks = useBookmarkStore((s) => s.bookmarks);
  const highlights = useHighlightStore((s) => s.highlights);
  const prayers = useAdiaStore((s) => s.prayers);
  const [state, setState] = useState<JournalSourcesState>('loading');
  const [sourcePolicy, setSourcePolicy] = useState<JournalSourcePolicy | null>(null);
  const [retryTick, setRetryTick] = useState(0);

  const retry = useCallback(() => {
    setState('loading');
    setSourcePolicy(null);
    setRetryTick((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        await waitForAuthResolution();
        if (cancelled) return;
        const policy = journalSourcePolicy(
          {
            bookmarks: BIBLE_BOOKMARKS_ENABLED,
            highlights: BIBLE_READER_V2_ENABLED,
            prayers: PRAY_MODE_ENABLED,
          },
          typeof getAuthUserId() === 'string',
        );
        setSourcePolicy(policy);
        const loads: Promise<unknown>[] = [];
        if (policy.loadBookmarks) {
          loads.push(useBookmarkStore.getState().loadBookmarks());
        }
        if (policy.loadHighlights) {
          loads.push(useHighlightStore.getState().loadHighlights());
        }
        if (policy.loadPrayers) {
          loads.push(useAdiaStore.getState().loadPrayers());
        }
        await Promise.all(loads);
        if (!cancelled) setState('ready');
      } catch (err: unknown) {
        console.warn('[journal] source load failed:', err);
        if (!cancelled) setState('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [retryTick]);

  const sources = useMemo<JournalSources>(
    () => ({
      bookmarks: sourcePolicy?.loadBookmarks ? bookmarks : [],
      highlights: sourcePolicy?.loadHighlights ? highlights : [],
      // Anchored prayers, including private ones, are an authenticated-owner
      // Journal source. A backend-free or signed-out fallback may show public
      // Bible text, but it never hydrates or searches authored Journal data.
      prayers: sourcePolicy?.loadPrayers ? prayers : [],
    }),
    [bookmarks, highlights, prayers, sourcePolicy],
  );

  return { sources, state, ready: state === 'ready', retry };
}
